import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, Output, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { CalendarModule } from 'primeng/calendar';
import { CardModule } from 'primeng/card';
import { DialogModule } from 'primeng/dialog';
import { DropdownModule } from 'primeng/dropdown';
import { InputTextModule } from 'primeng/inputtext';
import { InputTextareaModule } from 'primeng/inputtextarea';
import { HttpClientModule } from '@angular/common/http';
import { FundraiserService } from '../../services/fundraiser.service';
import { ToastModule } from 'primeng/toast';
import { MessageService } from 'primeng/api';
import { MainModel } from '../../../global/model/MainModel';
import Campaign from '../../../global/model/Campaign';

@Component({
  selector: 'app-fundraiser-modal',
  standalone: true,
  imports: [
    DialogModule,
    CardModule,
    ReactiveFormsModule,
    CommonModule,
    ButtonModule,
    InputTextModule,
    InputTextareaModule,
    DropdownModule,
    CalendarModule,
    HttpClientModule,
    ToastModule
  ],
  providers: [
    MessageService
  ],
  templateUrl: './fundraiser-modal.component.html',
  styleUrls: ['./fundraiser-modal.component.scss']
})
export class FundraiserModalComponent implements OnInit {
  @Input() visible: boolean = false;
  @Input() fundraiserData: any = null;
  @Output() onCancel = new EventEmitter<void>();
  @Output() onSave = new EventEmitter<any>();
  fundraiserForm: FormGroup;
  @Input() states: any[] = [];
  @Output() visibleChange = new EventEmitter<boolean>();

  campaignId: number = -1;

  constructor(
    private fb: FormBuilder,
    private fundraiserService: FundraiserService,
    private messageService: MessageService,
    private mainModel: MainModel
  ) {
    this.fundraiserForm = this.fb.group({
      firstName: ['', [Validators.required, Validators.minLength(2)]],
      lastName: ['', [Validators.required, Validators.minLength(2)]],
      trackingNumber: [''],
      email: ['', [Validators.email]],
      phone: ['', Validators.pattern(/^\d{10}$/)],
      address1: [''],
      address2: [''],
      city: [''],
      stateId: [''],
      zipCode: ['', [Validators.pattern(/^\d{5}$/)]],
      goal: ['', Validators.pattern(/^\d+$/)],
      parentTrackingId: [''],
    });
  }

  ngOnInit(): void {
    this.fetchCampaignId();
  }

  ngOnChanges(): void {
    if (this.fundraiserData) {
      this.fundraiserForm.patchValue(this.fundraiserData);
    } else {
      this.fundraiserForm.reset({
        firstName: '',
        lastName: '',
        trackingNumber: '',
        email: '',
        phone: '',
        address1: '',
        address2: '',
        city: '',
        stateId: '',
        zipCode: '',
        goal: '',
        parentTrackingId: '',
      });
    }
  }

  private fetchCampaignId(): void {
    const selectedCampaign = this.mainModel.selectedCampaign?.();
    if (!selectedCampaign) {
      this.mainModel.fetchSelectedCampaign().subscribe({
        next: (campaign: Campaign) => {
          this.mainModel.setSelectedCampaign?.(campaign);
          this.campaignId = campaign.campaignId;
        },
        error: (err) => {
          console.error('Failed to fetch selected campaign:', err);
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: 'Failed to fetch the selected campaign. Please try again later.',
            life: 5000,
          });
        },
      });
    } else {
      this.campaignId = selectedCampaign.campaignId;
    }

    if (isNaN(this.campaignId as number)) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Invalid Campaign ID',
        detail: 'The selected campaign ID is invalid. Please select a valid campaign.',
        life: 5000,
      });
    }
  }

  onDialogClose(): void {
    this.visible = false;
    this.visibleChange.emit(this.visible);
  }

  resetForm(): void {
    this.fundraiserForm.reset();
    Object.keys(this.fundraiserForm.controls).forEach(key => {
      this.fundraiserForm.get(key)?.setErrors(null);
    });
  }

  saveFundraiser(): void {
    if (this.fundraiserForm.valid) {
      const fundraiser = this.fundraiserForm.value;
      if (this.fundraiserData && this.fundraiserData.id) {
        // Update existing fundraiser
        this.fundraiserService.updateFundraiser(this.fundraiserData.id, fundraiser).subscribe(() => {
          this.onSave.emit();
        });
      } else {
        // Create new fundraiser
        this.fundraiserService.createFundraiser(fundraiser, this.campaignId).subscribe({
          next: (data: any) => {
            console.debug(data);
            const fundraiserName = `${fundraiser.firstName} ${fundraiser.lastName} ${data?.data?.trackingNumber ? `(${data.data.trackingNumber})` : ''}`.trim();
            const fundriaserData = {
              fundraiserName: fundraiserName,
              fundraiserId: data?.data?.fundraiserId
            }
            this.onSave.emit(fundriaserData);
          },
          error: () => {
            this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Error while saving fundraiser.' });
          }
        });
      }
    }
  }
}
